import { XbLogger } from '@amzn/xb-lite';
import { createXbLogger } from '@/src/utils/xbLogger';
import { Component, ReactNode } from 'react';

interface ErrorBoundaryProps {
  /** Component/page name emitted as the XB `ComponentName` dimension. */
  readonly componentName: string;
  readonly children: ReactNode;
  /** Optional UI rendered after a caught render error. Defaults to nothing. */
  readonly fallback?: ReactNode;
}

interface ErrorBoundaryState {
  readonly hasError: boolean;
}

/**
 * React error boundary that drives XB (Experience Bridge) availability metrics.
 *
 * - `pageLoad()` fires on mount (an attempted render of this subtree).
 * - `pageFailure()` fires when a descendant render throws.
 *
 * The dashboard availability metric is derived from the ratio of these two
 * signals, so a caught render error registers as an unavailable page load.
 * Recoverable/partial issues should be logged via `logger.error(...)` instead
 * of `pageFailure()` so they do not count against availability.
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  private readonly logger: XbLogger;

  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
    this.logger = createXbLogger(props.componentName);
  }

  componentDidMount(): void {
    // Successful mount = a page render was attempted/succeeded.
    this.logger.pageLoad();
  }

  static getDerivedStateFromError(): ErrorBoundaryState {
    return { hasError: true };
  }

  componentDidCatch(): void {
    // Full-page render failure — counts against availability. The underlying
    // JS error is also captured by RUM's `errors` telemetry, so it is not
    // re-logged here.
    this.logger.pageFailure();
  }

  render(): ReactNode {
    if (this.state.hasError) {
      return this.props.fallback ?? null;
    }
    return this.props.children;
  }
}

export default ErrorBoundary;
